import normalizeUrl from "normalize-url";
import { verifyEvent, getEventHash, matchFilter as nostrToolsMatchFilter } from 'nostr-tools';
import { cached } from "./LRUCache.mjs";
// ===========================================================================
// General-purpose
export const now = () => Math.round(Date.now() / 1000);
export const last = (xs) => xs[xs.length - 1];
export const identity = (x) => x;
// ===========================================================================
// Relays
export const stripProto = (url) => url.replace(/.*:\/\//, "");
export const isShareableRelay = (url) => {
    var _a;
    return Boolean(typeof url === 'string' &&
        // Is it actually a websocket url and has a dot
        url.match(/^wss?:\/\/.+\..+/) &&
        // Sometimes bugs cause multiple relays to get concatenated
        ((_a = url.match(/:\/\//g)) === null || _a === void 0 ? void 0 : _a.length) === 1 &&
        // It shouldn't have any whitespace
        !url.match(/\s/) &&
        // It shouldn't have any url-encoded whitespace
        !url.match(/%/) &&
        // Is it secure
        url.match(/^wss:\/\/.+/) &&
        // Don't match stuff with a port number
        !url.slice(6).match(/:\d+/) &&
        // Don't match raw ip addresses
        !url.slice(6).match(/\d+\.\d+\.\d+\.\d+/) &&
        // Skip nostr.wine's virtual relays
        !url.slice(6).match(/\/npub/));
};
export const normalizeRelayUrl = (url) => {
    // Use our library to normalize
    url = normalizeUrl(url, { stripHash: true, stripAuthentication: false });
    // Strip the protocol since only wss works
    url = stripProto(url);
    // Urls without pathnames are supposed to have a trailing slash
    if (!url.includes("/")) {
        url += "/";
    }
    return "wss://" + url;
};
// ===========================================================================
// Nostr URIs
export const fromNostrURI = (s) => s.replace(/^[\w+]+:\/?\/?/, "");
export const toNostrURI = (s) => `nostr:${s}`;
export const createEvent = (kind, { content = "", tags = [], created_at = now() }) => ({ kind, content, tags, created_at });
export const hasValidSignature = cached({
    maxSize: 10000,
    getKey: ([e]) => {
        try {
            return [getEventHash(e), e.sig].join(":");
        }
        catch (err) {
            return 'invalid';
        }
    },
    getValue: ([e]) => verifyEvent(e),
});
// ==========================================================================
// Tags
export class Fluent {
    constructor(xs) {
        this.as = (f) => f(this.xs);
        this.all = () => this.xs;
        this.count = () => this.xs.length;
        this.exists = () => this.xs.length > 0;
        this.first = () => this.xs[0];
        this.nth = (i) => this.xs[i];
        this.last = () => last(this.xs);
        this.flat = () => new Fluent(this.xs.flatMap(identity));
        this.uniq = () => new Fluent(Array.from(new Set(this.xs)));
        this.drop = (n) => new Fluent(this.xs.map(t => t.slice(n)));
        this.take = (n) => new Fluent(this.xs.map(t => t.slice(0, n)));
        this.map = (f) => new Fluent(this.xs.map(f));
        this.flatMap = (f) => new Fluent(this.xs.flatMap(f));
        this.pluck = (k) => new Fluent(this.xs.map(x => x[k]));
        this.filter = (f) => new Fluent(this.xs.filter(f));
        this.reject = (f) => new Fluent(this.xs.filter(t => !f(t)));
        this.any = (f) => this.filter(f).exists();
        this.find = (f) => this.xs.find(f);
        this.has = (x) => this.xs.includes(x);
        this.xs = xs.filter(identity);
    }
}
export class Tags extends Fluent {
    constructor() {
        super(...arguments);
        this.nthEq = (i, v) => new Tags(this.xs.filter(t => t[i] === v));
        this.values = (k) => this.filter(t => !k || t[0] === k).pluck(1);
        this.relays = () => this.flat().filter(isShareableRelay).uniq();
        this.topics = () => this.type("t").values().map((t) => t.replace(/^#/, ""));
        this.pubkeys = () => this.type("p").values();
        this.urls = () => this.type("r").values();
        this.getValue = (k) => this.values(k).first();
        this.roots = (type = null) => this.getAncestors(type).roots;
        this.replies = (type = null) => this.getAncestors(type).replies;
        this.groups = () => this.type("a").values().filter(a => a.startsWith('35834:'));
        this.communities = () => this.type("a").values().filter(a => a.startsWith('34550:'));
        this.circles = () => this.type("a").values().filter(a => a.match(/^(34550|35834):/));
        this.getReply = (type = null) => this.replies(type).values().first();
        this.getRoot = (type = null) => this.roots(type).values().first();
        this.getReplyHints = (type = null) => this.replies(type).relays().all();
        this.getRootHints = (type = null) => this.roots(type).relays().all();
    }
    static from(e) {
        const events = Array.isArray(e) ? e : [e];
        return new Tags(events.flatMap(e => e.tags));
    }
    type(t) {
        const types = Array.isArray(t) ? t : [t];
        return new Tags(this.xs.filter(t => types.includes(t[0])));
    }
    mark(m) {
        const marks = Array.isArray(m) ? m : [m];
        return new Tags(this.xs.filter(t => marks.includes(last(t))));
    }
    getDict() {
        const meta = {};
        for (const [k, v] of this.xs) {
            if (!meta[k]) {
                meta[k] = v;
            }
        }
        return meta;
    }
    getAncestorsLegacy() {
        // Legacy only supports e tags. Normalize their length to 3
        const eTags = this.type("e").map(t => {
            while (t.length < 3) {
                t.push("");
            }
            return t.slice(0, 3);
        });
        return {
            roots: eTags.count() > 1 ? new Tags([eTags.first()]) : new Tags([]),
            replies: new Tags([eTags.last()]),
            mentions: new Tags(eTags.all().slice(1, -1)),
        };
    }
    getAncestors(type = null) {
        // If we have a mark, we're not using the legacy format
        if (!this.any(t => t.length === 4 && ["reply", "root", "mention"].includes(last(t)))) {
            return this.getAncestorsLegacy();
        }
        const tags = new Tags(this.type(type || ["a", "e"]).all().filter(t => !String(t[1]).startsWith('34550:')));
        return {
            roots: new Tags(tags.mark('root').take(3).all()),
            replies: new Tags(tags.mark('reply').take(3).all()),
            mentions: new Tags(tags.mark('mention').take(3).all()),
        };
    }
}
export const matchFilter = (filter, event) => {
    if (!nostrToolsMatchFilter(filter, event)) {
        return false;
    }
    if (filter.search) {
        const content = event.content.toLowerCase();
        const terms = filter.search.toLowerCase().split(/\s+/g);
        for (const term of terms) {
            if (content.includes(term)) {
                return true;
            }
            return false;
        }
    }
    return true;
};
export const matchFilters = (filters, event) => {
    for (const filter of filters) {
        if (matchFilter(filter, event)) {
            return true;
        }
    }
    return false;
};
//# sourceMappingURL=nostr.mjs.map